import { supabase } from '@/integrations/supabase/client';
import { AssistantConfig } from './community-ai.service';
import { logInfo, logError, LogCategory } from '@/utils/logger';

/**
 * Service for handling AI mentions in community discussions
 */
export class AIMentionService {
  // Cache for AI assistants to avoid repeated database queries
  private static assistantsCache: AssistantConfig[] | null = null;
  private static cacheExpiry: number = 0;
  private static readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Get all active AI assistants with caching
   */
  static async getAllAssistants(): Promise<AssistantConfig[]> {
    const now = Date.now();
    
    // Return cached data if still valid
    if (this.assistantsCache && now < this.cacheExpiry) {
      return this.assistantsCache;
    }

    logInfo('Fetching all AI assistants for mention mapping', { 
      category: LogCategory.DATABASE 
    });

    try {
      const { data: assistants, error } = await supabase
        .from('agent_assistants')
        .select('id, name, openai_assistant_id, profile_picture_url, priority')
        .eq('is_active', true)
        .order('name');

      if (error) {
        throw error;
      }

      // Transform to AssistantConfig format
      const assistantConfigs: AssistantConfig[] = assistants?.map(assistant => ({
        id: assistant.id,
        name: assistant.name,
        openai_assistant_id: assistant.openai_assistant_id,
        profile_picture_url: assistant.profile_picture_url,
        priority: assistant.priority || 1,
        auto_respond: true, // Default for mentioned AIs
        response_delay_minutes: 0 // Immediate response for mentions
      })) || [];

      // Update cache
      this.assistantsCache = assistantConfigs;
      this.cacheExpiry = now + this.CACHE_DURATION;

      logInfo('Retrieved AI assistants for mention mapping', { 
        category: LogCategory.DATABASE,
        data: { assistantCount: assistantConfigs.length }
      });

      return assistantConfigs;
    } catch (error: any) {
      logError('Failed to fetch AI assistants for mention mapping', {
        category: LogCategory.DATABASE,
        data: { error: error.message }
      });
      return [];
    }
  }

  /**
   * Find assistant by name (case-insensitive)
   * @param mentionName - The name mentioned (without @)
   * @returns Assistant config if found, null otherwise
   */
  static async getAssistantByName(mentionName: string): Promise<AssistantConfig | null> {
    const assistants = await this.getAllAssistants();
    const normalizedMention = mentionName.toLowerCase().trim();
    
    // Find exact match first
    const exactMatch = assistants.find(
      assistant => assistant.name.toLowerCase() === normalizedMention
    );
    
    if (exactMatch) {
      logInfo('Found exact AI assistant match for mention', {
        category: LogCategory.DATABASE,
        data: { 
          mentionName, 
          assistantName: exactMatch.name,
          assistantId: exactMatch.id 
        }
      });
      return exactMatch;
    }

    // Find partial match (starts with)
    const partialMatch = assistants.find(
      assistant => assistant.name.toLowerCase().startsWith(normalizedMention)
    );

    if (partialMatch) {
      logInfo('Found partial AI assistant match for mention', {
        category: LogCategory.DATABASE,
        data: { 
          mentionName, 
          assistantName: partialMatch.name,
          assistantId: partialMatch.id 
        }
      });
      return partialMatch;
    }

    logInfo('No AI assistant found for mention', {
      category: LogCategory.DATABASE,
      data: { mentionName, availableAssistants: assistants.map(a => a.name) }
    });

    return null;
  }

  /**
   * Get multiple assistants by their mentioned names
   * @param mentionNames - Array of mentioned names
   * @returns Array of found assistant configs
   */
  static async getAssistantsByNames(mentionNames: string[]): Promise<AssistantConfig[]> {
    const foundAssistants: AssistantConfig[] = [];
    
    for (const mentionName of mentionNames) {
      const assistant = await this.getAssistantByName(mentionName);
      if (assistant) {
        foundAssistants.push(assistant);
      }
    }

    logInfo('Retrieved multiple AI assistants for mentions', {
      category: LogCategory.DATABASE,
      data: { 
        requestedMentions: mentionNames,
        foundCount: foundAssistants.length,
        foundAssistants: foundAssistants.map(a => a.name)
      }
    });

    return foundAssistants;
  }

  /**
   * Clear the assistants cache (useful for testing or when assistants are updated)
   */
  static clearCache(): void {
    this.assistantsCache = null;
    this.cacheExpiry = 0;
    logInfo('AI assistants cache cleared', { category: LogCategory.DATABASE });
  }
} 